import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FieldsetModule } from 'primeng/fieldset';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MetasFacade } from '../services/metas.facade';
import { TablaMetasComponent } from '../components/tabla-metas/tabla-metas.component';
import { MetaMasivaDialogComponent } from '../components/meta-masiva-dialog/meta-masiva-dialog.component';
import {
  META_MASIVA_ANIO_MAX_LENGTH,
  PLACEHOLDER_SELECCIONE
} from 'src/app/common/constants-ejb';
import {
  onlyNumbersKeypress,
  sanitizeNumbersOnly
} from 'src/app/common/input-utils';

/**
 * Container (smart component) de "Administración › Maestros › Metas" — Asignación Masiva de Metas Mes
 * (HU1290096).
 *
 * <p>Solo orquesta la UI y delega en {@link MetasFacade}. Filtro plegable (Oficina y Año), tabla en
 * `p-card` oculta hasta buscar con paginación manual por cursor (nunca `[paginator]="true"`) y modal
 * "Nueva Meta Masiva".</p>
 *
 * <p>Legacy: ListarMetasAnioViewImpl / ListarMetasAnioActivity.</p>
 */
@Component({
  selector: 'app-metas',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FieldsetModule,
    CardModule,
    ButtonModule,
    InputTextModule,
    DropdownModule,
    ProgressSpinnerModule,
    TablaMetasComponent,
    MetaMasivaDialogComponent
  ],
  providers: [MetasFacade],
  template: `
    <div class="p-3 sm:p-4 lg:p-6 max-w-7xl mx-auto overflow-x-hidden">
      <div class="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h1
          class="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100"
        >
          Asignación Masiva de Metas Mes
        </h1>
        <p-button
          label="Nuevo"
          icon="pi pi-plus"
          styleClass="min-h-[44px]"
          (onClick)="facade.abrirNuevo()"
        ></p-button>
      </div>

      <p-fieldset
        legend="Filtro Consulta de Metas"
        [toggleable]="true"
        styleClass="mb-4"
      >
        <div
          class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 sm:gap-x-6 gap-y-3 mb-3"
        >
          <div>
            <label
              for="filtro-oficina"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >Oficina *</label
            >
            <p-dropdown
              inputId="filtro-oficina"
              [options]="facade.oficinas()"
              optionLabel="dsoficina"
              optionValue="nmconsecutivo"
              [filter]="true"
              filterBy="dsoficina"
              [placeholder]="placeholder"
              appendTo="body"
              [style]="{ width: '100%' }"
              [(ngModel)]="facade.filtro.oficina"
            ></p-dropdown>
          </div>
          <div>
            <label
              for="filtro-anio"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >Ingrese el Año *</label
            >
            <input
              id="filtro-anio"
              pInputText
              class="w-full"
              aria-required="true"
              inputmode="numeric"
              autocomplete="off"
              [maxlength]="anioMaxLength"
              [(ngModel)]="facade.filtro.anio"
              (keypress)="onlyNumbersKeypress($event)"
              (input)="
                facade.filtro.anio = sanitizeNumbersOnly(facade.filtro.anio)
              "
              (keyup.enter)="facade.buscar()"
            />
          </div>
        </div>

        <div
          class="flex flex-wrap gap-2 sm:gap-3 justify-end sm:justify-start w-full"
        >
          <p-button
            label="Limpiar"
            icon="pi pi-eraser"
            severity="secondary"
            [outlined]="true"
            styleClass="min-h-[44px]"
            (onClick)="facade.limpiarFiltros()"
          ></p-button>
          <p-button
            label="Buscar"
            icon="pi pi-search"
            styleClass="min-h-[44px]"
            [loading]="facade.loading()"
            (onClick)="facade.buscar()"
          ></p-button>
        </div>
      </p-fieldset>

      @if (facade.mensaje()) {
        <div
          class="p-3 mb-4 rounded border whitespace-pre-line"
          role="alert"
          [ngClass]="{
            'bg-green-50 dark:bg-green-900/30 text-green-800 dark:text-green-200 border-green-200 dark:border-green-700':
              facade.mensajeTipo() === 'success',
            'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 border-yellow-200 dark:border-yellow-700':
              facade.mensajeTipo() === 'warning',
            'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-red-200 dark:border-red-700':
              facade.mensajeTipo() === 'error',
            'bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 border-blue-200 dark:border-blue-700':
              facade.mensajeTipo() === 'info'
          }"
        >
          {{ facade.mensaje() }}
        </div>
      }

      @if (facade.loading() && !facade.busquedaRealizada()) {
        <div class="flex justify-center p-4">
          <p-progressSpinner
            strokeWidth="4"
            [style]="{ width: '50px', height: '50px' }"
          ></p-progressSpinner>
        </div>
      }

      @if (facade.busquedaRealizada()) {
        <p-card styleClass="mb-4">
          <ng-template pTemplate="header">
            <div
              class="p-3 pb-0 flex justify-between items-center flex-wrap gap-2"
            >
              <h3
                class="text-lg font-semibold text-gray-700 dark:text-gray-200 m-0"
              >
                Listado Metas Mes
              </h3>
              @if (facade.totalElements() > 0) {
                <span class="text-sm text-gray-500 dark:text-gray-400">
                  Registros del {{ facade.registroInicio() }} hasta el
                  {{ facade.registroFin() }} de {{ facade.totalElements() }}
                </span>
              }
            </div>
          </ng-template>

          <app-tabla-metas
            [items]="facade.items()"
            [loading]="facade.loading()"
            [editandoId]="facade.editandoId()"
            (editar)="facade.iniciarEdicion($event)"
            (confirmar)="facade.modificarMeta($event.meta, $event.valor)"
            (cancelar)="facade.cancelarEdicion()"
          >
          </app-tabla-metas>

          @if (facade.totalElements() > 0) {
            <div
              class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 border-t dark:border-gray-700"
            >
              <span class="text-sm text-gray-600 dark:text-gray-300">
                Página {{ facade.currentPage() }} de {{ facade.totalPages() }}
              </span>
              <div
                class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3"
              >
                <p-button
                  label="Anterior"
                  icon="pi pi-angle-left"
                  severity="secondary"
                  [outlined]="true"
                  styleClass="min-h-[44px]"
                  [disabled]="!facade.hasPrevious() || facade.loading()"
                  (onClick)="facade.irPaginaAnterior()"
                ></p-button>
                <p-button
                  label="Siguiente"
                  icon="pi pi-angle-right"
                  iconPos="right"
                  severity="secondary"
                  [outlined]="true"
                  styleClass="min-h-[44px]"
                  [disabled]="!facade.hasNext() || facade.loading()"
                  (onClick)="facade.irPaginaSiguiente()"
                ></p-button>
              </div>
            </div>
          }
        </p-card>
      }
    </div>

    @if (facade.dialogoVisible()) {
      <app-meta-masiva-dialog
        [oficinas]="facade.oficinas()"
        [tiposAsesor]="facade.tiposAsesor()"
        [mensajes]="facade.mensajesDialogo()"
        [guardando]="facade.guardando()"
        (grabar)="facade.grabar($event)"
        (limpiar)="facade.limpiarMensajesDialogo()"
        (regresar)="facade.cerrarDialogo()"
      >
      </app-meta-masiva-dialog>
    }
  `
})
export class MetasComponent implements OnInit {
  protected readonly facade = inject(MetasFacade);

  protected readonly placeholder = PLACEHOLDER_SELECCIONE;
  protected readonly anioMaxLength = META_MASIVA_ANIO_MAX_LENGTH;
  protected readonly sanitizeNumbersOnly = sanitizeNumbersOnly;
  protected readonly onlyNumbersKeypress = onlyNumbersKeypress;

  ngOnInit(): void {
    void this.facade.inicializar();
  }
}
